import { useEffect, useRef } from 'react'

interface OutSideComponentsResponse {
  getReference: () => React.RefObject<HTMLDivElement>
}

/**
 * Problem: Sibling components may want to share the same click outside functionality (this prevents race conditions)
 * Solution: Have a single hook that has a reference factory, it can then check each produced reference and provide
 * the click outside functionality to all of them
 *
 * @example call getReference to be given a reference to use on each component to check
 *
 * @param initialIsVisible
 * @param onClose
 * @returns
 */
export const useClickOutsideComponents = (
  onClickOutside: () => void,
): OutSideComponentsResponse => {
  const refs = useRef<React.RefObject<HTMLDivElement>[]>([])

  // create reference factory
  const getRef = () => {
    const ref = useRef<HTMLDivElement>(null)
    refs.current.push(ref)
    return ref
  }

  // check if click is outside all references
  const handleClickOutside = (event: MouseEvent) => {
    let isOutsideAll = true

    refs.current.forEach((ref) => {
      if (ref.current && ref.current.contains(event.target as Node)) {
        isOutsideAll = false
      }
    })

    if (isOutsideAll) {
      onClickOutside()
    }
  }

  // hook into native click event
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  })

  // return reference factory
  return {
    getReference: getRef,
  }
}
